import { useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { IconClose } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import {
  ResizeHandle,
  useResizableDrawer,
} from "../../../hooks/useResizableDrawer";

type LeadCancellationState = {
  canceled: boolean;
  reason: PolicyCancellationReason | null;
  notes: string | null;
  cancellationRequestDate: string | null;
  effectiveDate: string | null;
};

type Props = {
  leadUlid: string;
  onClose: () => void;
  onApplied: () => void;
};

type PolicyCancellationReason =
  | "UNPAID_INVOICES"
  | "INCOMPLETE_CUSTOMER_ABANDONED"
  | "INCOMPLETE_NOT_SIGNED"
  | "INCOMPLETE_OPS_REJECTED"
  | "INCOMPLETE_INSURER_REJECTED"
  | "INCOMPLETE_DOCS"
  // Kept so historical leads cancelled with "Otro" still type-check when
  // loaded for edit, but it is no longer offered as a new option.
  | "INCOMPLETE_OTHER"
  | "RENEWAL";

const REASON_LABELS: { value: PolicyCancellationReason; label: string }[] = [
  { value: "UNPAID_INVOICES", label: "Impagos" },
  { value: "INCOMPLETE_CUSTOMER_ABANDONED", label: "Cliente abandona" },
  { value: "INCOMPLETE_NOT_SIGNED", label: "No firmó contrato" },
  { value: "INCOMPLETE_OPS_REJECTED", label: "Rechazada por Ops" },
  { value: "INCOMPLETE_INSURER_REJECTED", label: "Rechazada por aseguradora" },
  { value: "INCOMPLETE_DOCS", label: "Documentación pendiente" },
  { value: "RENEWAL", label: "Renovación" },
];

const today = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function CancellationDrawer({
  leadUlid,
  onClose,
  onApplied,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();

  const [reason, setReason] = useState<PolicyCancellationReason | "">("");
  const [notes, setNotes] = useState("");
  const [cancellationRequest, setCancellationRequest] = useState(today());
  const [effectiveDate, setEffectiveDate] = useState("");

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { width, startResize } = useResizableDrawer({ initial: 520, min: 360 });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request<LeadCancellationState>(
      `/api/admin/operations/leads/${leadUlid}/cancellation`,
      { token },
    )
      .then((current) => {
        if (cancelled) return;
        if (current.canceled) {
          setEditing(true);
          if (current.reason) setReason(current.reason);
          setNotes(current.notes ?? "");
          if (current.cancellationRequestDate) {
            setCancellationRequest(current.cancellationRequestDate);
          }
          setEffectiveDate(current.effectiveDate ?? "");
        }
      })
      .catch(() => {
        // Non-fatal: just treat as fresh cancellation.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadUlid, token]);

  const submit = async () => {
    if (submitting) return;
    if (!reason) {
      setError("Selecciona un motivo de cancelación");
      return;
    }
    if (!cancellationRequest) {
      setError("La fecha de solicitud es obligatoria");
      return;
    }
    // No constraint between effectiveDate and cancellationRequest on
    // purpose: ops occasionally executes a cancellation request with a
    // carrier-effective date earlier than the request itself (e.g. an
    // insurer back-dates the policy end-date to match a paid-up period).
    // The backend (CancelLeadController::parsePayload) already accepts
    // any combination of dates, so the previous client-side guard was
    // the only thing blocking the legitimate case.
    const reasonLabel =
      REASON_LABELS.find((r) => r.value === reason)?.label ?? reason;
    const ok = await confirm({
      title: editing ? "Actualizar cancelación" : "Cancelar póliza",
      message: editing
        ? `Se actualizarán los datos de la cancelación con motivo "${reasonLabel}". No se reenvía email al cliente. ¿Continuar?`
        : `Se cancelará el lead con motivo "${reasonLabel}" y se enviará el email automático al cliente. Esta acción no se puede deshacer. ¿Continuar?`,
      confirmLabel: editing ? "Guardar cambios" : "Cancelar póliza",
      tone: editing ? "warning" : "danger",
    });
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    try {
      await request(`/api/admin/operations/leads/${leadUlid}/cancel`, {
        method: "POST",
        token,
        body: {
          reason,
          notes: notes.trim() || undefined,
          cancellationRequest,
          effectiveDate: effectiveDate || undefined,
        },
      });
      onApplied();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${
              ((err.body as { message?: string })?.message ?? "").slice(0, 200) ||
              ((err.body as { error?: string })?.error ?? "")
            }`
          : "No se pudo cancelar",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerShell
      width={width}
      onStartResize={startResize}
      title={editing ? "Editar cancelación" : "Cancelar póliza"}
      onClose={onClose}
    >
      {loading && (
        <div style={{ color: "#73879C", fontSize: ".85rem", marginBottom: "0.5rem" }}>
          Cargando…
        </div>
      )}
      {editing && !loading && (
        <div
          style={{
            background: "#fff3cd",
            color: "#856404",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            fontSize: ".78rem",
            marginBottom: "0.85rem",
          }}
        >
          Este lead ya está cancelado. Puedes modificar motivo, notas y fechas. No
          se reenviará el email al cliente.
        </div>
      )}
      <Field label="Motivo de cancelación">
        <select
          value={reason}
          onChange={(e) =>
            setReason(e.target.value as PolicyCancellationReason | "")
          }
          style={selectStyle}
          disabled={submitting}
        >
          <option value="">— Selecciona un motivo —</option>
          {REASON_LABELS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Notas (opcional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Contexto adicional (visible internamente, no se envía al cliente)"
          style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }}
          disabled={submitting}
        />
      </Field>

      <Field label="Fecha de solicitud">
        <input
          type="date"
          value={cancellationRequest}
          onChange={(e) => setCancellationRequest(e.target.value)}
          style={inputStyle}
          disabled={submitting}
        />
      </Field>

      <Field label="Fecha efectiva carrier (opcional)">
        <input
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          style={inputStyle}
          disabled={submitting}
        />
      </Field>

      {error && <ErrorBox>{error}</ErrorBox>}

      <DrawerFooter
        onClose={onClose}
        onSubmit={submit}
        submitting={submitting}
        submitLabel={editing ? "Guardar cambios" : "Cancelar póliza"}
      />
    </DrawerShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: "0.3rem", marginBottom: "0.85rem" }}>
      <span
        style={{
          fontSize: ".72rem",
          color: "#73879C",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function DrawerShell({
  title,
  onClose,
  children,
  width,
  onStartResize,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  width: number;
  onStartResize: (event: React.MouseEvent) => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          height: "100%",
          background: "white",
          boxShadow: "-2px 0 16px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          // Reset table-cell inheritance: parent <td> forces nowrap on
          // descendants even across position: fixed boundaries.
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        <ResizeHandle onMouseDown={onStartResize} />
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.95rem 1.1rem",
            borderBottom: "1px solid #e9ecef",
            gap: "0.75rem",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933", flex: 1 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} style={closeBtn}>
            <IconClose />
          </button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function DrawerFooter({
  onClose,
  onSubmit,
  submitting,
  submitLabel,
}: {
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        justifyContent: "flex-end",
        marginTop: "1.1rem",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        style={ghostBtn}
        disabled={submitting}
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={onSubmit}
        style={dangerBtn}
        disabled={submitting}
      >
        {submitting ? "Cancelando…" : submitLabel}
      </button>
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: "0.7rem",
        padding: "0.5rem 0.7rem",
        borderRadius: "0.4rem",
        background: "#fde2e2",
        color: "#b32424",
        fontSize: ".8rem",
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.45rem 0.6rem",
  fontSize: ".88rem",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  background: "#f8f9fb",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: "auto",
};

const closeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const dangerBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#b32424",
  color: "white",
  border: "1px solid #b32424",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
